<template>
  <div class="task-node" :class="[`task-node--level-${task.hierarchy_level ? task.hierarchy_level.toLowerCase() : 'task'}`, { 'task-node--root': depth === 0 }]">
    <!-- Task Card -->
    <div class="pp-task-card" :class="{ 'pp-task-card--expanded': isExpanded, 'pp-task-card--has-error': hasError }">
      <!-- Card Header / Summary Row -->
      <div class="pp-task-card__header" @click="toggleExpand">
        <div class="pp-task-card__left">
          <!-- Expand/Collapse Chevron -->
          <button
            type="button"
            class="pp-btn-icon pp-chevron-btn"
            :class="{ 'rotated': isExpanded }"
            :aria-label="isExpanded ? 'Collapse' : 'Expand'"
            @click.stop="toggleExpand"
          >
            <i class="fas fa-chevron-right"></i>
          </button>

          <!-- Sequence Badge -->
          <span class="pp-sequence-badge" title="Sequence Order">#{{ task.sequence_order + 1 }}</span>

          <!-- Hierarchy Level Badge -->
          <span class="pp-hierarchy-badge" :class="hierarchyBadgeClass">{{ task.hierarchy_level || 'Task' }}</span>

          <!-- Task Title Display or Quick Input -->
          <div class="pp-task-title-wrap">
            <span
              v-if="!isEditingTitle"
              class="pp-task-title-text"
              :class="{ 'text-muted fst-italic': !task.title }"
              @dblclick.stop="startEditTitle"
            >
              {{ task.title || '(Untitled Task - double click to edit)' }}
            </span>
            <input
              v-else
              ref="titleInputRef"
              :value="task.title"
              type="text"
              class="pp-form-input pp-task-title-input"
              placeholder="Task Title"
              @input="updateField('title', $event.target.value)"
              @blur="finishEditTitle"
              @keyup.enter="finishEditTitle"
              @click.stop
            />
          </div>
        </div>

        <!-- Badges & Action Buttons -->
        <div class="pp-task-card__right">
          <div class="pp-task-card__badges">
            <!-- Execution Mode -->
            <span class="pp-meta-pill" :class="task.execution_mode === 'Concurrent' ? 'pp-meta-pill--orange' : 'pp-meta-pill--indigo'">
              <i class="fas" :class="task.execution_mode === 'Concurrent' ? 'fa-arrows-split-up-and-left' : 'fa-arrow-down-1-9'"></i>
              {{ task.execution_mode || 'Sequential' }}
            </span>

            <!-- Frequency -->
            <span v-if="task.frequency && task.frequency !== 'One-off'" class="pp-meta-pill pp-meta-pill--purple">
              <i class="fas fa-repeat"></i> {{ task.frequency }}
            </span>

            <!-- Assignee / Dynamic Role -->
            <span class="pp-meta-pill pp-meta-pill--blue" :title="assignmentSummary">
              <i class="fas" :class="task.assignment_type === 'Static' ? 'fa-user' : 'fa-user-tag'"></i>
              {{ assignmentSummary }}
            </span>

            <!-- Approval Gate -->
            <span v-if="task.is_approval_gate" class="pp-meta-pill pp-meta-pill--amber" :title="`Approval Gate: ${task.approval_role || 'Sign-off Required'}`">
              <i class="fas fa-shield-halved"></i> Gate: {{ task.approval_role || 'Sign-off' }}
            </span>

            <!-- Integration Target -->
            <span v-if="task.integration_target && task.integration_target !== 'None'" class="pp-meta-pill pp-meta-pill--teal">
              <i class="fas fa-plug"></i> {{ task.integration_target }}
            </span>
          </div>

          <!-- Quick Action Buttons -->
          <div class="pp-task-card__actions" @click.stop>
            <button
              v-if="depth === 0"
              type="button"
              class="pp-task-action-btn pp-task-action-btn--add"
              title="Add Sub-task"
              @click="addChildTask"
            >
              <i class="fas fa-plus"></i>
            </button>
            <button
              type="button"
              class="pp-task-action-btn"
              :disabled="index === 0"
              title="Move Up"
              @click="moveUp"
            >
              <i class="fas fa-arrow-up"></i>
            </button>
            <button
              type="button"
              class="pp-task-action-btn"
              :disabled="isLastChild"
              title="Move Down"
              @click="moveDown"
            >
              <i class="fas fa-arrow-down"></i>
            </button>
            <button
              type="button"
              class="pp-task-action-btn pp-task-action-btn--delete"
              title="Delete Task"
              @click="removeSelf"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Card Body / Detail Form (Collapsible) -->
      <div v-show="isExpanded" class="pp-task-card__body">
        <div class="row g-3">
          <!-- Task Title -->
          <div class="col-12 col-md-6">
            <label class="pp-form-label required">Task Title</label>
            <input
              :value="task.title"
              type="text"
              class="pp-form-input"
              :class="{ 'is-invalid': hasError }"
              placeholder="e.g. Schedule Initial Kickoff Call"
              @input="updateField('title', $event.target.value)"
            />
            <div v-if="hasError" class="text-danger small mt-1">Task Title is required.</div>
          </div>

          <!-- Execution Mode -->
          <div class="col-12 col-md-3">
            <label class="pp-form-label">Execution Mode</label>
            <select :value="task.execution_mode || 'Sequential'" class="pp-form-select" @change="updateField('execution_mode', $event.target.value)">
              <option v-for="mode in executionModes" :key="mode.name || mode" :value="mode.name || mode">
                {{ mode.display_name || mode }}
              </option>
            </select>
          </div>

          <!-- Frequency -->
          <div class="col-12 col-md-3">
            <label class="pp-form-label">Frequency</label>
            <select :value="task.frequency || 'One-off'" class="pp-form-select" @change="updateField('frequency', $event.target.value)">
              <option v-for="freq in frequencies" :key="freq.name || freq" :value="freq.name || freq">
                {{ freq.display_name || freq }}
              </option>
            </select>
          </div>

          <!-- Description -->
          <div class="col-12">
            <label class="pp-form-label">Detailed Instructions / Description</label>
            <textarea
              :value="task.description"
              class="pp-form-input"
              rows="2"
              placeholder="Provide execution steps or guidance for the assigned team member..."
              @input="updateField('description', $event.target.value)"
            ></textarea>
          </div>

          <!-- Assignment Type -->
          <div class="col-12 col-md-4">
            <label class="pp-form-label">Assignment Type</label>
            <select :value="task.assignment_type || 'Dynamic'" class="pp-form-select" @change="handleAssignmentTypeChange($event.target.value)">
              <option value="Dynamic">Dynamic (Role-Based)</option>
              <option value="Static">Static (Specific User)</option>
            </select>
          </div>

          <!-- Dynamic Role Selector (if Dynamic) -->
          <div v-if="task.assignment_type !== 'Static'" class="col-12 col-md-4">
            <label class="pp-form-label">Dynamic Role</label>
            <select :value="task.dynamic_role || 'Vertex Organisation Lead'" class="pp-form-select" @change="updateField('dynamic_role', $event.target.value)">
              <option v-for="role in dynamicRoles" :key="role.name || role" :value="role.name || role">
                {{ role.display_name || role }}
              </option>
            </select>
          </div>

          <!-- Static User Selector (if Static) -->
          <div v-else class="col-12 col-md-4">
            <label class="pp-form-label required">Assigned User</label>
            <select
              :value="task.static_user_id"
              class="pp-form-select"
              :class="{ 'is-invalid': submitted && task.assignment_type === 'Static' && !task.static_user_id }"
              @change="updateField('static_user_id', $event.target.value)"
            >
              <option value="">-- Select Team Member / BDM --</option>
              <option v-for="user in internalUsers" :key="user.id" :value="user.id">
                {{ user.full_name || user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email }}
              </option>
            </select>
            <div v-if="submitted && task.assignment_type === 'Static' && !task.static_user_id" class="text-danger small mt-1">
              Assigned User is required.
            </div>
          </div>

          <!-- Approval Gate Toggle -->
          <div class="col-12 col-md-4 d-flex flex-column justify-content-center">
            <label class="pp-form-label">Approval Gate</label>
            <label class="pp-toggle-switch mt-1">
              <input
                :checked="task.is_approval_gate"
                type="checkbox"
                @change="handleApprovalGateToggle($event.target.checked)"
              />
              <span class="pp-toggle-slider"></span>
              <span class="pp-toggle-label">{{ task.is_approval_gate ? 'Enabled (Requires Sign-off)' : 'Disabled' }}</span>
            </label>
          </div>

          <!-- Approver Role / Owner Selector (when Approval Gate is enabled) -->
          <div v-if="task.is_approval_gate" class="col-12 col-md-4">
            <label class="pp-form-label required">Approver Role</label>
            <select
              :value="task.approval_role || 'Super Admin'"
              class="pp-form-select"
              :class="{ 'is-invalid': submitted && task.is_approval_gate && !task.approval_role }"
              @change="updateField('approval_role', $event.target.value)"
            >
              <option value="">-- Select Approver Role --</option>
              <option v-for="role in dynamicRoles" :key="role.name || role" :value="role.name || role">
                {{ role.display_name || role }}
              </option>
            </select>
            <div v-if="submitted && task.is_approval_gate && !task.approval_role" class="text-danger small mt-1">
              Approver Role is required for Approval Gates.
            </div>
          </div>

          <!-- Integration Target -->
          <div class="col-12 col-md-4">
            <label class="pp-form-label">Integration Target</label>
            <select :value="task.integration_target || 'None'" class="pp-form-select" @change="handleIntegrationChange($event.target.value)">
              <option v-for="target in integrationTargets" :key="target.name || target" :value="target.name || target">
                {{ target.display_name || target }}
              </option>
            </select>
          </div>

          <!-- Conditionally Exposed Payload Configuration -->
          <div v-if="task.integration_target && task.integration_target !== 'None'" class="col-12">
            <div class="pp-payload-box">
              <div class="pp-payload-box__header d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div class="d-flex align-items-center gap-2">
                  <span class="pp-meta-pill pp-meta-pill--teal">
                    <i class="fas fa-plug me-1"></i> {{ task.integration_target }} Configuration
                  </span>
                  <span class="text-muted small">JSON / Parameters / Webhook Payload</span>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button
                    v-if="hasSamplePayload"
                    type="button"
                    class="pp-btn pp-btn--ghost pp-btn--sm"
                    @click="prefillSample"
                  >
                    <i class="fas fa-wand-magic-sparkles me-1"></i> Prefill Template
                  </button>
                  <button
                    type="button"
                    class="pp-btn pp-btn--ghost pp-btn--sm"
                    @click="formatJson"
                  >
                    <i class="fas fa-indent me-1"></i> Format JSON
                  </button>
                </div>
              </div>

              <!-- Vault Security Note -->
              <div class="pp-vault-note">
                <i class="fas fa-shield-alt text-amber me-1"></i>
                <span>Use vault key placeholders (e.g. <code>&#123;&#123;SECRET_VAULT_KEY&#125;&#125;</code>) rather than plaintext tokens for secure credentials.</span>
              </div>

              <textarea
                :value="task.payload_data"
                class="pp-payload-textarea mt-2"
                rows="4"
                placeholder='{ "template_id": "...", "field_mappings": { ... } }'
                @input="updateField('payload_data', $event.target.value)"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recursive Sub-task Container -->
    <div v-if="task.sub_tasks && task.sub_tasks.length > 0" class="pp-subtask-container">
      <TaskTreeNode
        v-for="(sub, subIdx) in task.sub_tasks"
        :key="sub.id || subIdx"
        :task="sub"
        :parent-task="task"
        :index="subIdx"
        :depth="depth + 1"
        :total-siblings="task.sub_tasks.length"
        :hierarchy-levels="hierarchyLevels"
        :execution-modes="executionModes"
        :assignment-types="assignmentTypes"
        :dynamic-roles="dynamicRoles"
        :frequencies="frequencies"
        :integration-targets="integrationTargets"
        :internal-users="internalUsers"
        :submitted="submitted"
        @add-child="$emit('add-child', $event)"
        @remove-task="$emit('remove-task', $event)"
        @move-up="$emit('move-up', $event)"
        @move-down="$emit('move-down', $event)"
        @update-field="$emit('update-field', $event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "TaskTreeNode",
  props: {
    task: {
      type: Object,
      required: true,
    },
    parentTask: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: 0,
    },
    depth: {
      type: Number,
      default: 0,
    },
    totalSiblings: {
      type: Number,
      default: 0,
    },
    hierarchyLevels: {
      type: Array,
      default: () => ["Task", "Sub-task"],
    },
    executionModes: {
      type: Array,
      default: () => ["Sequential", "Concurrent"],
    },
    assignmentTypes: {
      type: Array,
      default: () => ["Dynamic", "Static"],
    },
    dynamicRoles: {
      type: Array,
      default: () => ["Vertex Organisation Lead", "BDM", "Super Admin"],
    },
    frequencies: {
      type: Array,
      default: () => [
        "One-off", "Daily", "Weekly", "Fortnightly", "Monthly",
        "Quarterly", "Biannually", "Annually", "Every 2 Years",
        "Every 3 Years", "Every 4 Years", "Every 5 Years",
      ],
    },
    integrationTargets: {
      type: Array,
      default: () => [
        "None", "Tallyfy", "Pentester", "Alke", "SOC", "Confluence", "Google Forms", "Custom Webhook",
      ],
    },
    internalUsers: {
      type: Array,
      default: () => [],
    },
    submitted: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["add-child", "remove-task", "move-up", "move-down", "update-field", "update:task"],
  data() {
    return {
      isExpanded: true,
      isEditingTitle: false,
    };
  },
  computed: {
    isLastChild() {
      if (this.parentTask && this.parentTask.sub_tasks) {
        return this.index === this.parentTask.sub_tasks.length - 1;
      }
      if (this.totalSiblings > 0) {
        return this.index === this.totalSiblings - 1;
      }
      return false;
    },
    hasError() {
      return (
        (this.submitted && !this.task.title) ||
        (this.submitted && this.task.assignment_type === "Static" && !this.task.static_user_id) ||
        (this.submitted && this.task.is_approval_gate && !this.task.approval_role)
      );
    },
    hierarchyBadgeClass() {
      const level = (this.task.hierarchy_level || "Task").toLowerCase();
      if (level === "job") return "pp-hierarchy-badge--job";
      if (level === "sub-task" || level === "subtask") return "pp-hierarchy-badge--subtask";
      return "pp-hierarchy-badge--task";
    },
    assignmentSummary() {
      if (this.task.assignment_type === "Static") {
        if (this.task.static_user_id) {
          const user = this.internalUsers.find((u) => String(u.id) === String(this.task.static_user_id));
          if (user) return user.full_name || user.name || user.email || "Assigned User";
        }
        return "Unassigned User";
      }
      return this.task.dynamic_role || "Vertex Organisation Lead";
    },
    hasSamplePayload() {
      const target = this.task.integration_target;
      return target && target !== "None";
    },
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    startEditTitle() {
      this.isEditingTitle = true;
      this.$nextTick(() => {
        if (this.$refs.titleInputRef) {
          this.$refs.titleInputRef.focus();
        }
      });
    },
    finishEditTitle() {
      this.isEditingTitle = false;
    },
    updateField(field, value) {
      this.$emit("update-field", { task: this.task, field, value });
    },
    handleAssignmentTypeChange(newType) {
      this.updateField("assignment_type", newType);
      if (newType === "Dynamic" && !this.task.dynamic_role) {
        this.updateField("dynamic_role", "Vertex Organisation Lead");
      }
    },
    handleApprovalGateToggle(checked) {
      this.updateField("is_approval_gate", checked);
      if (checked && !this.task.approval_role) {
        this.updateField("approval_role", "Super Admin");
      }
    },
    handleIntegrationChange(newTarget) {
      this.updateField("integration_target", newTarget);
      if (newTarget === "None") {
        this.updateField("payload_data", "");
      }
    },
    addChildTask() {
      const childLevel = "Sub-task";
      this.$emit("add-child", { parentTask: this.task, hierarchyLevel: childLevel });
      this.isExpanded = true;
    },
    removeSelf() {
      this.$emit("remove-task", this.task);
    },
    moveUp() {
      this.$emit("move-up", { task: this.task, parentTask: this.parentTask });
    },
    moveDown() {
      this.$emit("move-down", { task: this.task, parentTask: this.parentTask });
    },
    prefillSample() {
      const target = this.task.integration_target;
      let sample = "";
      if (target === "Tallyfy") {
        sample = JSON.stringify({
          process_id: "proc_sample_id",
          template_id: "tpl_sample_id",
          public_template_schema: "v2",
          process_name: "Customer Onboarding - {{CLIENT_NAME}}",
          field_mappings: {
            organization_name: "{{CLIENT_NAME}}",
            primary_contact_email: "{{CONTACT_EMAIL}}",
          },
        }, null, 2);
      } else if (target === "Pentester") {
        sample = JSON.stringify({
          action: "Create Test",
          test_type: "Network & Web Application Pentest",
          target_scope: "{{CLIENT_DOMAIN}}",
          lead_consultant_role: "Lead Penetration Tester",
        }, null, 2);
      } else if (target === "Alke") {
        sample = JSON.stringify({
          action: "Create Audit",
          audit_framework: "ISO 27001 / Essential Eight",
          organization_name: "{{CLIENT_NAME}}",
          lead_auditor_role: "Vertex Organisation Lead",
          api_key_ref: "{{ALKE_VAULT_KEY}}",
        }, null, 2);
      } else if (target === "SOC") {
        sample = JSON.stringify({
          action: "Create Log Source",
          siem_tenant_id: "{{CLIENT_TENANT_ID}}",
          log_collector_type: "Syslog / M365 Defender",
          retention_days: 365,
          api_token_ref: "{{SOC_VAULT_KEY}}",
        }, null, 2);
      } else if (target === "Confluence") {
        sample = JSON.stringify({
          space_key: "CLIENTDELIVERY",
          parent_page_url: "https://confluence.vertexcybersecurity.com.au/display/CLIENTDELIVERY/Projects",
          page_template: "Standard Service Handover Runbook",
          page_title: "{{CLIENT_NAME}} - {{SERVICE_NAME}} Runbook",
        }, null, 2);
      } else if (target === "Google Forms") {
        sample = JSON.stringify({
          form_url: "https://docs.google.com/forms/d/e/1FAIpQLSd_sample_form_id/viewform",
          field_mappings: {
            "entry.100001": "{{CLIENT_NAME}}",
            "entry.100002": "{{CONTACT_EMAIL}}",
            "entry.100003": "{{JOB_REFERENCE}}",
          },
        }, null, 2);
      } else if (target === "Custom Webhook") {
        sample = JSON.stringify({
          webhook_url: "https://api.example.com/events/job-staged",
          http_method: "POST",
          auth_header: "Bearer {{SECRET_VAULT_KEY}}",
        }, null, 2);
      } else {
        sample = JSON.stringify({
          target: target,
          parameters: {},
        }, null, 2);
      }
      this.updateField("payload_data", sample);
    },
    formatJson() {
      if (!this.task.payload_data) return;
      try {
        const parsed = JSON.parse(this.task.payload_data);
        this.updateField("payload_data", JSON.stringify(parsed, null, 2));
      } catch (e) {
        // Leave as is if not valid JSON
      }
    },
  },
};
</script>

<style scoped>
.task-node {
  margin-bottom: 12px;
}

.pp-task-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #008298;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  overflow: hidden;
}

.task-node--level-subtask .pp-task-card,
.task-node--level-sub-task .pp-task-card {
  border-left-color: #3b82f6;
}

.pp-task-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 3px 8px rgba(0, 130, 152, 0.08);
}

.pp-task-card--expanded {
  border-color: #008298;
}

.pp-task-card--has-error {
  border-color: #ef4444 !important;
}

/* Card Header */
.pp-task-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  background-color: #ffffff;
  gap: 12px;
  flex-wrap: wrap;
}

.pp-task-card__left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 260px;
  min-width: 0;
}

.pp-btn-icon {
  background: none;
  border: none;
  color: #64748b;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
}

.pp-btn-icon:hover {
  background: #f1f5f9;
  color: #008298;
}

.pp-chevron-btn.rotated {
  transform: rotate(90deg);
}

.pp-sequence-badge {
  font-size: 0.75rem;
  font-weight: 700;
  background: #f1f5f9;
  color: #475569;
  padding: 3px 8px;
  border-radius: 6px;
}

.pp-hierarchy-badge {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  text-transform: uppercase;
}

.pp-hierarchy-badge--job {
  background: #eef2ff;
  color: #4338ca;
}

.pp-hierarchy-badge--task {
  background: #e6f7fa;
  color: #008298;
}

.pp-hierarchy-badge--subtask {
  background: #eff6ff;
  color: #2563eb;
}

.pp-task-title-wrap {
  flex: 1;
  min-width: 150px;
}

.pp-task-title-text {
  font-weight: 700;
  color: #1e293b;
  font-size: 0.9rem;
}

.pp-task-card__right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pp-task-card__badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.pp-meta-pill {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pp-meta-pill--indigo {
  background: #eef2ff;
  color: #4338ca;
}

.pp-meta-pill--orange {
  background: #fff7ed;
  color: #c2410c;
}

.pp-meta-pill--purple {
  background: #faf5ff;
  color: #7e22ce;
}

.pp-meta-pill--blue {
  background: #eff6ff;
  color: #1d4ed8;
}

.pp-meta-pill--amber {
  background: #fffbeb;
  color: #b45309;
}

.pp-meta-pill--teal {
  background: #f0fdfa;
  color: #0f766e;
}

.pp-task-card__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pp-task-action-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: all 0.15s ease;
}

.pp-task-action-btn:hover:not(:disabled) {
  background: #f8fafc;
  color: #008298;
  border-color: #cbd5e1;
}

.pp-task-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pp-task-action-btn--add {
  color: #008298;
}

.pp-task-action-btn--add:hover {
  background: #e6f7fa;
  color: #008298;
}

.pp-task-action-btn--delete {
  color: #ef4444;
}

.pp-task-action-btn--delete:hover {
  background: #fee2e2;
  color: #dc2626;
  border-color: #fca5a5;
}

/* Card Body */
.pp-task-card__body {
  padding: 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

/* Form Controls */
.pp-form-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
  display: block;
}

.pp-form-label.required::after {
  content: " *";
  color: #ef4444;
}

.pp-form-input, .pp-form-select {
  width: 100%;
  padding: 8px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #1e293b;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s;
}

.pp-form-input:focus, .pp-form-select:focus {
  border-color: #00A0C1;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
}

.pp-toggle-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.pp-toggle-switch input {
  display: none;
}

.pp-toggle-slider {
  width: 40px;
  height: 22px;
  background-color: #cbd5e1;
  border-radius: 22px;
  position: relative;
  transition: background-color 0.2s;
}

.pp-toggle-slider::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background-color: #ffffff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.pp-toggle-switch input:checked + .pp-toggle-slider {
  background-color: #00A0C1;
}

.pp-toggle-switch input:checked + .pp-toggle-slider::after {
  transform: translateX(18px);
}

.pp-toggle-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}

/* Payload Box */
.pp-payload-box {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
}

.pp-payload-textarea {
  width: 100%;
  font-family: monospace;
  font-size: 0.8rem;
  background: #0f172a;
  color: #38bdf8;
  border-radius: 6px;
  padding: 10px;
  border: 1px solid #334155;
  outline: none;
}

.pp-vault-note {
  font-size: 0.78rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 6px;
  padding: 6px 10px;
  margin-top: 6px;
}

.pp-subtask-container {
  margin-top: 10px;
  margin-left: 24px;
  padding-left: 14px;
  border-left: 2px dashed #cbd5e1;
}

.pp-btn {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.pp-btn--primary {
  background-color: #00A0C1;
  border-color: #00A0C1;
  color: #ffffff;
}

.pp-btn--primary:hover {
  background-color: #008298;
  border-color: #008298;
}

.pp-btn--ghost {
  background-color: transparent;
  color: #008298;
  border: 1px solid #cbd5e1;
}

.pp-btn--ghost:hover {
  background-color: #f1f5f9;
  border-color: #94a3b8;
  color: #006070;
}
</style>
